import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Props as ImpressionProps } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { Paper, OpenalexWork, Author } from 'js/social/shared/types';
import { NoThumbnailIcon } from 'js/social/shared/Icons';
import { TruncateHeight } from 'js/social/shared/SocialEditor/TruncateHeight';
import { Attachment } from 'js/liswp/build_vu/Attachment';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';
import _PaperThumbnail from "PaperAbstract-PaperThumbnail.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperThumbnail";
import _ContentAndButtonsContainer from "PaperAbstract-ContentAndButtonsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=ContentAndButtonsContainer";
import _ContentContainer from "PaperAbstract-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=ContentContainer";
import _PaperTitle from "PaperAbstract-PaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperTitle";
import _Abstract from "PaperAbstract-Abstract.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=Abstract";
import _SeeMoreButton from "PaperAbstract-SeeMoreButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=SeeMoreButton";
import _Container from "PaperAbstract-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=Container";
import _AttachmentContainer from "PaperAbstract-AttachmentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=AttachmentContainer";
import _PaperAuthorsAndJournal from "PaperAbstract-PaperAuthorsAndJournal.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperAuthorsAndJournal";
import _AuthorLink from "PaperAbstract-AuthorLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=AuthorLink";


export type PaperSize = 'xsmall' | 'small' | 'medium' | 'large';

const LINE_HEIGHT = 20;

const PaperThumbnail = /*#__PURE__*/styled("div", null, {
  displayName: "PaperThumbnail",
  styles: _PaperThumbnail
});

const ContentAndButtonsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentAndButtonsContainer",
  styles: _ContentAndButtonsContainer
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const PaperTitle = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "PaperTitle",
  styles: _PaperTitle
});

const Abstract = /*#__PURE__*/styled(Body, null, {
  displayName: "Abstract",
  styles: _Abstract
});

const SeeMoreButton = /*#__PURE__*/styled(Abstract, null, {
  displayName: "SeeMoreButton",
  styles: _SeeMoreButton
});

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const AttachmentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "AttachmentContainer",
  styles: _AttachmentContainer
});

const PaperAuthorsAndJournal = /*#__PURE__*/styled("div", null, {
  displayName: "PaperAuthorsAndJournal",
  styles: _PaperAuthorsAndJournal
});

const AuthorLink = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "AuthorLink",
  styles: _AuthorLink
});

const PaperAuthor = ({
  author,
  size,
}: {
  author: Author;
  size?: 'xs' | 'sm';
}) =>
  author.profileUrl ? (
    <AuthorLink
      href={author.profileUrl}
      target={'_blank'}
      size={size}
      onClick={(e) => e.stopPropagation()}
    >
      {author.displayName}
    </AuthorLink>
  ) : (
    <Body as="span" size={size}>
      {author.displayName}
    </Body>
  );

type Props = {
  work: Paper | OpenalexWork;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  size?: 'sm' | 'md';
  showSummary?: boolean;
  showPreview?: boolean;
  isSnippet?: boolean;
  variant?: 'default' | 'dense';
};

const PaperContainer: React.FunctionComponent<Props> = ({
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  size = 'md',
  showSummary = true,
  showPreview = false,
  isSnippet = false,
  variant = 'default',
}) => {
  const {
    url,
    title,
    authors,
    publicationYear,
    publication,
    attachment,
    summary,
  } = work;

  const publicationInfo: string[] = [];
  if (publicationYear) publicationInfo.push(publicationYear.toString());
  if (publication) publicationInfo.push(publication);

  const thumbnailImage =
    attachment?.thumbnailUrl != null ? (
      <img src={attachment.thumbnailUrl} alt="" />
    ) : (
      <NoThumbnailIcon />
    );

  let abstractHeightMultiplier = size === 'sm' ? 2 : 3;
  if (variant === 'dense') {
    // For mobile, show 2 lines in the abstract
    abstractHeightMultiplier = window.innerWidth > 567 ? 1 : 2;
  }

  const handleCardClick = () => {
    if (variant === 'dense') {
      canUseReader ? showReader() : window.open(url, '_blank');
    }
  };

  const content = (
    <>
      {showPreview && attachment ? null : canUseReader ? (
        <PaperThumbnail onClick={showReader} size={size} variant={variant}>
          {thumbnailImage}
        </PaperThumbnail>
      ) : (
        <PaperThumbnail
          as="a"
          href={url}
          target={'_blank'}
          size={size}
          variant={variant}
        >
          {thumbnailImage}
        </PaperThumbnail>
      )}
      <ContentAndButtonsContainer onClick={handleCardClick}>
        <ContentContainer
          data-coachmark-step="abstract-read-more"
          variant={variant}
        >
          <ImpressionWatcher
            stopPropagation={false}
            {...impressionPropsGen('work-card')}
          >
            {canUseReader ? (
              <PaperTitle size={size} onClick={showReader} variant={variant}>
                {title}
              </PaperTitle>
            ) : (
              <PaperTitle
                size={size}
                href={url}
                target={'_blank'}
                variant={variant}
              >
                {title}
              </PaperTitle>
            )}
            <PaperAuthorsAndJournal isSnippet={isSnippet} variant={variant}>
              {authors?.length > 0 && (
                <Body size={'sm'}>
                  by{' '}
                  {authors
                    .map((a) => (
                      <PaperAuthor
                        key={`${a.userId}:${a.displayName}`}
                        author={a}
                        size={'sm'}
                      />
                    ))
                    .flatMap((el) => [el, ', '])
                    .slice(0, -1)}
                </Body>
              )}
              {publicationInfo.length > 0 && (
                <Body size={'sm'}> {publicationInfo.join(', ')} </Body>
              )}
            </PaperAuthorsAndJournal>
            {showSummary && summary && (
              <TruncateHeight
                height={LINE_HEIGHT * abstractHeightMultiplier}
                readMoreImpressionProps={impressionPropsGen('see-more')}
                styledMoreElement={<SeeMoreButton variant={variant} />}
                verb="Read"
              >
                <Abstract size={'sm'}> Abstract: {summary} </Abstract>
              </TruncateHeight>
            )}
            {showPreview && attachment && (
              <AttachmentContainer onClick={showReader}>
                <Attachment
                  key={attachment.id.toString()}
                  shown
                  pages={1}
                  attachmentId={attachment.id.toString()}
                />
              </AttachmentContainer>
            )}
          </ImpressionWatcher>
        </ContentContainer>

        {work.podcast && (
          <SimplePaperPodcast
            workId={work.podcast.workId}
            workTitle={work.podcast.workTitle}
            showTitle={false}
            impressionSource="paper_to_podcast_newsfeed"
          />
        )}

        {paperInteractions}
      </ContentAndButtonsContainer>
    </>
  );

  return (
    <Container size={size} variant={variant}>
      {content}
    </Container>
  );
};

export default PaperContainer;
